import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTravelTranslation } from '../../app/TravelLocaleContext';
import { adsMemberApi, generateClientRequestId } from '../../api/adsMemberApi';
import './ads.css';

export default function CreativeCreatePage() {
  const { promotionId } = useParams();
  const { t } = useTravelTranslation();
  const navigate = useNavigate();
  const clientRequestIdRef = useRef(generateClientRequestId());

  const [promotion, setPromotion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [headline, setHeadline] = useState('');
  const [summary, setSummary] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    adsMemberApi.promotions
      .get(promotionId)
      .then((data) => {
        if (active) {
          setPromotion(data);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (active) {
          setError(err?.message || '載入推廣資料失敗');
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [promotionId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!headline.trim()) {
      setError('請輸入廣告標題');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const res = await adsMemberApi.creatives.create(promotionId, {
        headline: headline.trim(),
        summary: summary.trim(),
        clientRequestId: clientRequestIdRef.current,
      });
      navigate(`/me/ads/creatives/${res.creativeId}`);
    } catch (err) {
      setError(err?.message || '建立廣告素材失敗');
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <main className="ads-page" data-testid="creative-create-loading">
        <div className="ads-loading">{t('travel.ads.common.loading')}</div>
      </main>
    );
  }

  const isApproved = promotion?.status === 'APPROVED';

  return (
    <main className="ads-page" data-testid="creative-create-page">
      <header className="ads-header">
        <h1 className="ads-title">{t('travel.ads.creatives.create')}</h1>
        <Link
          to={`/me/ads/promotions/${promotionId}`}
          className="ads-btn ads-btn--secondary"
          data-testid="btn-back-promotion"
        >
          返回推廣詳情
        </Link>
      </header>

      {!isApproved ? (
        <div className="ads-alert ads-alert--notice" data-testid="creative-create-not-approved">
          推廣申請尚未通過審核（當前狀態：{promotion?.status}），無法建立素材。
        </div>
      ) : (
        <form className="ads-form" onSubmit={handleSubmit} data-testid="creative-create-form">
          {error ? (
            <div className="ads-alert ads-alert--error" data-testid="creative-create-error">
              {error}
            </div>
          ) : null}

          <div className="ads-form-group">
            <label className="ads-form-label" htmlFor="headline">
              {t('travel.ads.creatives.headline')}
            </label>
            <input
              id="headline"
              type="text"
              maxLength={100}
              className="ads-form-input"
              value={headline}
              onChange={(e) => setHeadline(e.target.value)}
              placeholder="例如: 精選私房京都慢活行程"
              disabled={submitting}
              data-testid="input-creative-headline"
            />
          </div>

          <div className="ads-form-group">
            <label className="ads-form-label" htmlFor="summary">
              {t('travel.ads.creatives.summary')}
            </label>
            <textarea
              id="summary"
              maxLength={500}
              className="ads-form-textarea"
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              placeholder="簡述廣告宣傳內容與亮點"
              disabled={submitting}
              data-testid="input-creative-summary"
            />
          </div>

          <div>
            <button
              type="submit"
              className="ads-btn ads-btn--primary"
              disabled={submitting}
              data-testid="btn-submit-creative-create"
            >
              {submitting ? t('travel.ads.common.submitting') : t('travel.ads.common.save')}
            </button>
          </div>
        </form>
      )}
    </main>
  );
}
